import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Scale, Calendar, Clock, Check, X, Users } from "lucide-react";
import { formatDate } from "@/lib/format";
import logoMark from "@/assets/logo-mark.png";

// Mantido "Reunião" aqui de propósito (ver comentário equivalente em EVENT_LABEL no
// server.py) — essa página usa o rótulo dentro de frase feminina ("a Reunião marcada"),
// "Outro" quebraria a concordância. Só o seletor/etiqueta do evento no "Novo evento" e nas
// listas virou "Outro" (Lote 56).
const EVENT_LABEL = { audiencia: "Audiência", prazo: "Prazo", reuniao: "Reunião" };

export default function TeamNoticePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");

  const load = async () => {
    try {
      const r = await http.get(`/public/team/${token}`);
      setInfo(r.data);
      setName(r.data.recipient_name || "");
    } catch (e) {
      setError({ expired: e?.response?.status === 410, msg: e?.response?.data?.detail || "Link inválido" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const ack = async () => {
    setSubmitting(true);
    try {
      await http.post(`/public/team/${token}/ack`, { name: name || null });
      toast.success("Recebimento confirmado");
      load();
    } catch (e) {
      if (e?.response?.status === 410) { load(); return; }
      toast.error(e?.response?.data?.detail || "Erro");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Scale className="w-6 h-6 animate-pulse text-stone-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <div className="max-w-md w-full text-center bg-white border border-stone-200 rounded-md p-8">
          <X className="w-10 h-10 mx-auto text-rose-brand mb-3" strokeWidth={1.5} />
          <h1 data-testid="link-erro-titulo" className="font-display text-xl font-bold text-stone-900 mb-2">{error.expired ? "Este link expirou" : "Link inválido"}</h1>
          <p data-testid="link-erro-texto" className="text-sm text-stone-500">{error.expired ? "Para tratar deste compromisso, fale diretamente com o escritório." : error.msg}</p>
        </div>
      </div>
    );
  }

  const h = info.hearing || {};
  const alreadyAck = Boolean(info.ack_at);

  return (
    <div className="min-h-screen bg-stone-50 py-12 px-4">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <img src={logoMark} alt="" className="w-8 h-8 rounded-md" />
          <span className="font-display font-bold text-stone-900 text-lg"><span className="text-gold-600">IA</span>pauta</span>
        </div>

        <div className="bg-white border border-stone-200 rounded-md p-8">
          <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-emerald-brand mb-2 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" strokeWidth={2} /> Aviso da equipe
          </div>
          <h1 className="font-display text-2xl font-bold text-stone-900 mb-2">
            {info.action || "Você tem um aviso"}
          </h1>
          <p className="text-sm text-stone-500 mb-6">
            {EVENT_LABEL[h.event_type] || "Compromisso"} referente ao processo abaixo.
          </p>

          <div className="space-y-3 mb-6">
            <Row icon={Calendar} label="Data" value={formatDate(h.date)} />
            <Row icon={Clock} label="Horário" value={h.time} />
            {(h.parties || h.description) && <Row label="Quem / o quê" value={h.parties || h.description} />}
            {h.court && <Row label="Vara / Comarca" value={h.court} />}
            {h.case_number && <Row label="Processo" value={h.case_number} />}
          </div>

          {alreadyAck ? (
            <div className="bg-stone-100 border border-stone-200 rounded-md p-4 text-center">
              <Check className="w-6 h-6 mx-auto text-emerald-brand mb-1" strokeWidth={2} />
              <p className="text-sm font-semibold text-stone-900">
                Recebimento confirmado por {info.ack_by}
              </p>
              <p className="text-xs text-stone-500 mt-1">
                Registrado em {new Date(info.ack_at).toLocaleString("pt-BR")}
              </p>
            </div>
          ) : (
            <>
              <input
                data-testid="team-ack-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Seu nome (opcional)"
                className="w-full text-sm p-3 rounded-md border border-stone-200 focus:outline-none focus:ring-2 focus:ring-emerald-brand mb-4"
              />
              <Button
                data-testid="team-ack-button"
                onClick={ack}
                disabled={submitting}
                className="w-full bg-emerald-brand text-white gap-2"
              >
                <Check className="w-4 h-4" strokeWidth={2} /> Recebi, vou providenciar
              </Button>
            </>
          )}
        </div>

        <p className="text-center text-xs text-stone-400 mt-6">
          Recebeu por engano? Basta ignorar este link.
        </p>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    {Icon && <Icon className="w-4 h-4 text-stone-400 mt-0.5 shrink-0" strokeWidth={1.5} />}
    <div className="flex-1 flex justify-between gap-4">
      <span className="text-xs uppercase tracking-widest text-stone-500 mt-0.5">{label}</span>
      <span className="text-sm text-stone-900 font-medium text-right">{value || "—"}</span>
    </div>
  </div>
);
